import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Monitor, Trash2 } from 'lucide-react';
import EditDeviceModal from '../modals/EditDeviceModal';
import AssignClassToDeviceModal from '../modals/AssignClassToDeviceModal';
import { Device } from '../../../types/admin';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge, Button } from '@/components/shared/ui';
import { formatLocaleDateShort } from '@/lib/localeFormat';

interface DeviceListProps {
    devices: Device[];
    /** 기관 ID — 클래스–기기 할당 모달의 device-allocation API */
    organizationId?: number;
    /** 클래스–기기 연결 후 (예: 기기 목록 refetch) */
    onAssignClassComplete?: () => void;
    /** 기관명 */
    organizationName?: string;
    /** true면 프로덕트 카드는 부모(예: OrgPage)에서만 표시 */
    suppressProductCards?: boolean;
    showStats?: boolean;
    /** `Promise`를 반환하면 resolve 후 모달이 닫힙니다. (API 성공/변경없음) */
    onDeviceUpdate?: (device: Device) => void | Promise<void>;
    onDeviceDelete?: (deviceId: string) => void;
}

/** 프로덕트 카드 1건 */
type ProductCardItem =
    { productName: string; activeCount: number; totalCount: number; source: 'model' };

export default function DeviceList({
    devices,
    organizationId,
    onAssignClassComplete,
    suppressProductCards = false,
    showStats = true,
    onDeviceUpdate,
    onDeviceDelete,
}: DeviceListProps): React.ReactElement {
    const { t, i18n } = useTranslation('admin');
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [isAssignClassModalOpen, setIsAssignClassModalOpen] = useState<boolean>(false);
    const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
    const [selectedProductName, setSelectedProductName] = useState<string | null>(null);

    // 모델 기반 프로덕트 카드
    const modelBasedProductCards = useMemo((): ProductCardItem[] => {
        const byProduct = new Map<string, { active: number; total: number }>();
        for (const d of devices) {
            const key = d.modelName || t('deviceList.other');
            const cur = byProduct.get(key) ?? { active: 0, total: 0 };
            cur.total += 1;
            if (d.status === 'active') cur.active += 1;
            byProduct.set(key, cur);
        }
        return Array.from(byProduct.entries()).map(([productName, { active, total }]) => ({
            productName,
            activeCount: active,
            totalCount: total,
            source: 'model' as const,
        }));
    }, [devices]);

    const productCards = modelBasedProductCards;

    // 프로덕트 선택 시 해당 모델만 필터
    const filteredDevices = useMemo(() => {
        if (!selectedProductName) return devices;
        return devices.filter((d) => (d.modelName || t('deviceList.other')) === selectedProductName);
    }, [devices, selectedProductName, t]);

    const handleProductCardClick = (productName: string) => {
        setSelectedProductName((prev) => (prev === productName ? null : productName));
    };

    // 통계: 활성 기기만 현재 대수로 카운트
    const deviceStats = useMemo(() => {
        const active = devices.filter(d => d.status === 'active').length;
        const total = devices.length;
        return {
            total,
            active,
            inactive: total - active,
        };
    }, [devices]);

    const handleEdit = (device: Device) => {
        setSelectedDevice(device);
        setIsEditModalOpen(true);
    };

    const handleDelete = (device: Device) => {
        if (window.confirm(`"${device.deviceName}" ${t('deviceList.deleteConfirm')}`)) {
            onDeviceDelete?.(device.id as string);
        }
    };

    const handleSave = (updatedDevice: Device) => {
        void (async () => {
            if (!onDeviceUpdate) {
                setIsEditModalOpen(false);
                setSelectedDevice(null);
                return;
            }
            const out = onDeviceUpdate(updatedDevice);
            if (out == null) return;
            if (typeof (out as Promise<void>).then !== 'function') return;
            try {
                await (out as Promise<void>);
            } catch {
                return;
            }
            setIsEditModalOpen(false);
            setSelectedDevice(null);
        })();
    };

    const getStatusBadge = (status: string) => {
        const badges: { [key: string]: { label: string; variant: 'statusActive' | 'statusInactive' } } = {
            active: { label: t('deviceList.statusActive'), variant: 'statusActive' },
            inactive: { label: t('deviceList.statusInactive'), variant: 'statusInactive' }
        };
        return badges[status] || badges.active;
    };

    return (
        <>
            {/* 프로덕트 카드: 모델별 기기 현황 */}
            {!suppressProductCards && productCards.length > 0 && (
                <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {productCards.map((card) => {
                        const isSelected = selectedProductName === card.productName;
                        return (
                            <button
                                key={card.productName}
                                type="button"
                                onClick={() => handleProductCardClick(card.productName)}
                                className={`rounded-xl border p-4 shadow-sm flex items-center gap-4 text-left w-full transition-colors ${
                                    isSelected
                                        ? 'border-brand-300 bg-brand-50 ring-1 ring-brand-500/30'
                                        : 'border-brand-100 bg-white hover:ring-2 hover:ring-brand-500/30'
                                }`}
                                aria-pressed={isSelected}
                                aria-label={`${card.productName} ${t('deviceList.viewDevices')}`}
                            >
                                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${isSelected ? 'bg-brand-500 text-white' : 'bg-brand-50 text-brand-600'}`}>
                                    <Monitor className="w-5 h-5" />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-gray-900 truncate mb-2">{card.productName}</p>
                                    <p className="text-sm text-gray-500">
                                        {t('deviceList.inUse')} <span className="font-semibold text-brand-600">{card.activeCount}</span>
                                        <span className="mx-1">/</span>
                                        {t('deviceList.maximum')} <span className="font-medium text-gray-700">{card.totalCount}</span>
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {showStats && (
                <div className="mb-6 flex justify-between items-center px-2 pt-4">
                    <div className="flex gap-6 items-center">
                        <div className="flex items-center gap-3">
                            <span className="text-base font-medium text-gray-500">{t('deviceList.inUse')}</span>
                            <span className="text-base font-bold text-brand-500">{deviceStats.active}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="text-base font-medium text-gray-500">{t('deviceList.maximum')}</span>
                            <span className="text-base font-bold text-brand-500">{deviceStats.total}</span>
                        </div>
                    </div>
                    <Button
                        variant="primary"
                        onClick={() => setIsAssignClassModalOpen(true)}
                    >
                        {t('deviceList.assignClass')}
                    </Button>
                </div>
            )}

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-gray-50/50">
                            <TableHead>{t('deviceList.deviceName')}</TableHead>
                            <TableHead>{t('deviceList.modelName')}</TableHead>
                            <TableHead>{t('deviceList.purchaseSource')}</TableHead>
                            <TableHead>{t('deviceList.registeredDate')}</TableHead>
                            <TableHead>{t('deviceList.lastConnection')}</TableHead>
                            <TableHead>{t('deviceList.status')}</TableHead>
                            <TableHead>{t('deviceList.manage')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filteredDevices.length > 0 ? (
                            filteredDevices.map((device) => {
                                const statusBadge = getStatusBadge(device.status);
                                return (
                                    <TableRow key={device.id} className="hover:bg-gray-50/50 transition-colors">
                                        <TableCell className="font-semibold text-gray-900">{device.deviceName}</TableCell>
                                        <TableCell>
                                            <Badge variant="default">{device.modelName}</Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-600">{device.purchaseSource}</TableCell>
                                        <TableCell className="text-sm text-gray-600">{formatLocaleDateShort(device.registeredDate, i18n.language)}</TableCell>
                                        <TableCell className="text-sm text-gray-600">{device.lastConnection || '-'}</TableCell>
                                        <TableCell>
                                            <Badge variant={statusBadge.variant}>
                                                {statusBadge.label}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <button
                                                    className="p-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
                                                    onClick={() => handleEdit(device)}
                                                    title={t('deviceList.edit')}
                                                >
                                                    <Edit size={16} />
                                                </button>
                                                <button
                                                    className="p-2 hover:bg-red-50 rounded-md transition-colors text-red-600 hover:text-red-700"
                                                    onClick={() => handleDelete(device)}
                                                    title={t('deviceList.delete')}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-12 text-gray-500">
                                    {selectedProductName ? t('deviceList.noDevicesForProduct') : t('deviceList.noDevices')}
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <EditDeviceModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                device={selectedDevice}
                onSave={handleSave}
            />

            {isAssignClassModalOpen && (
                <AssignClassToDeviceModal
                    isOpen
                    onClose={() => setIsAssignClassModalOpen(false)}
                    organizationId={organizationId}
                    devices={devices}
                    onAssigned={onAssignClassComplete}
                />
            )}
        </>
    );
}
